import { useState, useEffect, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import {
  MAINTENANCE_MODE_FLAG,
  getHeaderValue,
  isApiRequestUrl,
  parseMaintenanceRefusal,
  recordMaintenanceRefusal,
} from './maintenance';
import { PLATFORM_FLAGS_ENDPOINT, usePlatformFlags } from './usePlatformFlags';

/**
 * Whether `url` is the release-flags poll itself.
 *
 * The poll must never be able to trigger the takeover. It targets
 * `/api/v1/static/flags`, which `isApiRequestUrl` matches like any other
 * platform call, so a bare 503 from Grieg would latch the takeover — and the
 * takeover unmounts the app, which is what runs the poll. The flag going off
 * again could then never be observed, so the one channel that ends the state
 * would have been disabled by the state itself (NS-2462).
 *
 * Matched on path rather than on the raw string so both the relative and
 * absolute forms are recognised.
 */
const isPlatformFlagsUrl = (url?: string): boolean => {
  if (!url) return false;

  try {
    return new URL(url, window.location.origin).pathname === PLATFORM_FLAGS_ENDPOINT;
  } catch {
    return false;
  }
};

/**
 * Registers an Axios response interceptor that discriminates between the two
 * things a 503 can mean, and returns true only for the second.
 *
 * - A **typed maintenance 503** (a JSON body carrying `code: "MAINTENANCE"`) is
 *   a deliberate read-only window. It is recorded as a refusal so the UI can
 *   explain it, and this hook keeps returning false — the app stays browsable
 *   behind the maintenance banner instead of blacking out. Without this, under
 *   the read-only gate a single refused write would take the whole app down.
 * - **Any other 503** from a platform API is an unexplained failure, i.e. a
 *   genuine outage, and still returns true so the caller renders the
 *   full-screen maintenance/outage page exactly as before. That preserves the
 *   de facto error fallback this interceptor has always provided, which is why
 *   the takeover is discriminated rather than deleted (NS-2461).
 *
 * The one exception is a typed window that reports `readsAvailable: false`:
 * nothing is being served, so the takeover is the honest presentation.
 *
 * **Ending a window.** The takeover used to latch for the life of the page —
 * nothing ever set it back — so a user present when a window started stayed
 * locked out after it ended until they reloaded, which they had no reason to
 * think would help. It now clears when the maintenance flag transitions from on
 * to off, driven by the flags poll rather than by another failed request, so
 * recovery needs no user action (NS-2462).
 *
 * Clearing is deliberately keyed on the **transition**, not on the flag being
 * false. A takeover caused by a genuine outage happens while the flag is false
 * throughout, so there is no transition and this does not touch it — outage
 * recovery is unchanged and remains the outage page's own concern.
 *
 * @param onWindowEnded - Called once when a window ends, after the takeover has
 * been cleared. Used by the caller to refresh data that was fetched or blocked
 * during the window; this library has no query client of its own.
 * @returns Whether the caller should render the full-screen outage page.
 */
export function useMaintenanceMode(onWindowEnded?: () => void): boolean {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);

  const flags = usePlatformFlags();
  const isMaintenanceFlagOn = Boolean(flags[MAINTENANCE_MODE_FLAG]);
  const wasMaintenanceFlagOn = useRef(false);

  // Held in a ref so a caller passing an inline callback does not re-run the
  // transition effect on every render and fire the callback repeatedly.
  const onWindowEndedRef = useRef(onWindowEnded);

  useEffect(() => {
    onWindowEndedRef.current = onWindowEnded;
  }, [onWindowEnded]);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => {
        if (err?.response?.status === 503) {
          // The flags poll is never allowed to drive the takeover — see
          // isPlatformFlagsUrl.
          const mayTakeOver = !isPlatformFlagsUrl(err.config?.url);

          const refusal = parseMaintenanceRefusal({
            status: err.response.status,
            body: err.response.data,
            retryAfter: getHeaderValue(err.response.headers, 'retry-after'),
          });

          if (refusal) {
            recordMaintenanceRefusal(refusal);
            // A read-only window must not black the app out; a window with no
            // reads available has nothing to show, so it still takes over.
            if (!refusal.readsAvailable && mayTakeOver) setIsMaintenanceMode(true);
          } else if (mayTakeOver && isApiRequestUrl(err.config?.url)) {
            setIsMaintenanceMode(true);
          }
        }

        return Promise.reject(err);
      },
    );

    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    if (wasMaintenanceFlagOn.current && !isMaintenanceFlagOn) {
      setIsMaintenanceMode(false);
      onWindowEndedRef.current?.();
    }

    wasMaintenanceFlagOn.current = isMaintenanceFlagOn;
  }, [isMaintenanceFlagOn]);

  return isMaintenanceMode;
}
